import "./syncInspector.scss";

import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormButton from "../../components/form/FormButton";
import FormEnableDropdown from "../../components/form/FormEnableDropdown";
import FormList from "../../components/form/FormList";
import InspectorEmptyBox from "../../components/inspector/InspectorEmptyBox";
import InspectorField from "../../components/inspector/InspectorField";
import InspectorGroup from "../../components/inspector/InspectorGroup";
import InspectorHeader from "../../components/inspector/InspectorHeader";
import InspectorRow from "../../components/inspector/InspectorRow";
import Icon from "../../components/interface/Icon";
import PubSubTopics from "../../constants/pubSubTopics";
import { syncMessages } from "../../constants/syncMessages";
import { syncStatusOptions, syncStatusProperties } from "../../constants/syncStatus";
import SyncTypeOptions from "../../constants/SyncTypeOptions";
import syncUpdateActions from "../../constants/syncUpdateActions";
import { appStateOperations } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import { batchActions } from "../../modules/chainReducer";
import { syncOperations, syncSelectors } from "../../modules/sync";
import { syncSettingsSelectors } from "../../modules/sync/settings";
import syncValueComparisons from "../../sync/utils/syncValueComparisons";
import { immutableRecords } from "../../types";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import { standardizeLines } from "../../utils/stringUtils";
import selectors from "./selectors";

const onSelectItem = (useProps, itemId) => {
  PubSub.publish(PubSubTopics.scrollToItem, { id: itemId });
  useProps.onSelectItems([itemId]);
};

const displayForStatus = (props, status) => {
  const statusProps = syncStatusProperties[status];
  const label = syncSelectors.getSyncStatusLabelForStatus(status, props.syncApplicationName);
  const icon = status === syncStatusOptions.DETECTED_NO_CHANGE ? "statusValid" : statusProps.statusIcon;
  const color = status === syncStatusOptions.DETECTED_NO_CHANGE ? "statusValid" : statusProps.statusColor;
  return {
    label,
    icon,
    color
  };
};

const instructionsForStatus = status => {
  let instructions = "";
  switch (status) {
    case syncStatusOptions.DETECTED_CONFLICT:
      instructions = syncMessages.CONFLICT;
      break;
    case syncStatusOptions.DETECTED_IGNORE:
      instructions = syncMessages.IGNORED;
      break;
    case syncStatusOptions.DETECTED_NO_CHANGE:
      instructions = syncMessages.NO_CHANGE;
      break;
    default:
      instructions = syncMessages.CHANGED;
  }
  return instructions;
};

const renderManuscriptGroup = props => {
  return (
    <InspectorEmptyBox
      title={props.destinationPath.last()}
      content={`Select a document within the ${props.destinationPath.last()} folder to view sync details`}
      onCollapseClick={props.onCollapseClick}
    />
  );
};

const renderTopGroup = props => {
  const icon = (
    <Icon
      className="syncTypeIcon"
      icon={props.iconForTreeItem}
      color={props.externalTypeColor ? props.externalTypeColor : "iconMid"}
    />
  );
  let title = null;
  if (!props.externalLabel) {
    title = <span>{props.localLabel}</span>;
  } else {
    title = <span title={props.destinationPath.join(" > ")}>{props.externalLabel}</span>;
  }

  // if (!props.localLabel) {
  //   title = <span title={props.destinationPath.join(" > ")}>{props.externalLabel}</span>;
  // } else {
  //   title = <span>{props.localLabel}</span>;
  // }

  return (
    <InspectorGroup>
      <InspectorHeader>
        {icon}
        {title}
        <Icon
          icon="rightArrow"
          className="inspectorCollapseArrow"
          color="iconMono"
          onClick={props.onCollapseClick}
          canFocus={false}
        />
      </InspectorHeader>
    </InspectorGroup>
  );
};

const renderSyncStatusGroup = props => {
  const statusProps = displayForStatus(props, props.status);
  const statusInstructions = instructionsForStatus(props.status);
  let statusMessages = [
    {
      value: "status",
      label: statusProps.label,
      subLabel: statusInstructions,
      leftIcon: statusProps.icon,
      leftIconColor: statusProps.color
    }
  ];

  return (
    <InspectorGroup>
      <div className="syncInspectorMessages">
        <FormList id="syncInspectorMessages" values={statusMessages} canDelete={false} canAdd={false} />
      </div>
    </InspectorGroup>
  );
};

const renderLinkGroup = props => {
  // TODO: Pass correct value for hasChildren
  const showExternalOptions = props.unlinkedDocumentOptions && props.unlinkedDocumentOptions.size > 1;
  const showLocalOptions = props.unlinkedItemOptions && props.unlinkedItemOptions.size > 1;

  let changeDropdownStyle = { minWidth: "150px", maxWidth: "300px" };
  //#!if mobile === true
// changeDropdownStyle = null;
  //#!endif

  const onChangeExternal = value => {
    props.onChangeLink(props.localId, SyncTypeOptions.CREATE ? null : value, props.localId, props.externalId);
  };

  const onChangeLocal = value => {
    props.onChangeLink(
      value === SyncTypeOptions.CREATE ? props.localId : value,
      value === SyncTypeOptions.CREATE ? null : props.externalId,
      props.localId,
      props.externalId
    );
  };

  return (
    <div className="syncInspectorLinkedItems">
      <InspectorGroup label="Linked Items">
        <InspectorRow label={`${props.syncApplicationName}`} verticalCenter={true}>
          {showExternalOptions ? (
            <InspectorField>
              <FormEnableDropdown
                options={props.unlinkedDocumentOptions.toArray()}
                value={props.externalId ? props.externalId : SyncTypeOptions.CREATE}
                onChange={onChangeExternal}
                useParentWidth={false}
                style={changeDropdownStyle}
              />
            </InspectorField>
          ) : (
            <div className="syncLinkValue">
              <Icon className="syncTypeIcon" icon={props.externalTypeIcon} color={props.externalTypeColor} />
              <span title={props.destinationPath.join(" > ")}>{getLabelWithUntitled(props.externalLabel)}</span>
            </div>
          )}
        </InspectorRow>
        <InspectorRow label={`Timeline`} verticalCenter={true}>
          {showLocalOptions ? (
            <InspectorField>
              <FormEnableDropdown
                options={props.unlinkedItemOptions.toArray()}
                value={
                  props.isIgnored ? SyncTypeOptions.IGNORE : props.localId ? props.localId : SyncTypeOptions.CREATE
                }
                onChange={onChangeLocal}
                useParentWidth={false}
                style={changeDropdownStyle}
                allowSearch={true}
              />
            </InspectorField>
          ) : (
            <div className="syncLinkValue" onClick={() => onSelectItem(props, props.localId)}>
              <Icon
                className="syncTypeIcon"
                icon={props.localTypeIcon ? props.localTypeIcon : "blank"}
                color={props.localTypeColor ? props.localTypeColor : "mono"}
              />
              <span>{getLabelWithUntitled(props.localLabel)}</span>
            </div>
          )}
        </InspectorRow>
      </InspectorGroup>
    </div>
  );
};

const renderPropertyConflict = (props, change) => {
  const statusProps = displayForStatus(props, change.status);

  return (
    <Fragment key={change.id}>
      <InspectorHeader>
        <Icon icon={statusProps.icon} className="inspectorStatusIcon" color={statusProps.color} />
        <span>
          {change.label}{" "}
          {change.status === syncStatusOptions.DETECTED_CONFLICT ? "has diverged" : "will be resolved on sync"}:
        </span>
      </InspectorHeader>
      {!syncValueComparisons.valuesAreEqual(change.lastSyncValue, change.externalValue) ? (
        <InspectorRow
          label={props.syncApplicationName}
          brightLabel={
            change.action === syncUpdateActions.APPLY_EXTERNAL_TO_LOCAL ||
            change.status === syncStatusOptions.DETECTED_CONFLICT
          }
        >
          <div className="syncProperty">
            <div
              className={
                change.action !== syncUpdateActions.APPLY_EXTERNAL_TO_LOCAL &&
                change.status !== syncStatusOptions.DETECTED_CONFLICT
                  ? "oldValue"
                  : "newValue"
              }
            >
              {standardizeLines(change.externalValue)}
            </div>
            {change.action !== syncUpdateActions.APPLY_EXTERNAL_TO_LOCAL ? (
              <FormButton
                label={`Use from ${props.syncApplicationName}`}
                linkStyle={true}
                onClick={() => props.onChooseExternalClick(props.id, change.id)}
                primary={true}
              />
            ) : (
              "(Selected)"
            )}
          </div>
        </InspectorRow>
      ) : null}
      {!syncValueComparisons.valuesAreEqual(change.lastSyncValue, change.localValue) ? (
        <InspectorRow
          label="Timeline"
          brightLabel={
            change.action === syncUpdateActions.APPLY_LOCAL_TO_EXTERNAL ||
            change.status === syncStatusOptions.DETECTED_CONFLICT
          }
        >
          <div className="syncProperty">
            <div
              className={
                change.action !== syncUpdateActions.APPLY_LOCAL_TO_EXTERNAL &&
                change.status !== syncStatusOptions.DETECTED_CONFLICT
                  ? "oldValue"
                  : "newValue"
              }
            >
              {change.localValue}
            </div>
            {change.action !== syncUpdateActions.APPLY_LOCAL_TO_EXTERNAL ? (
              <FormButton
                label={`Use from Timeline`}
                linkStyle={true}
                onClick={() => props.onChooseLocalClick(props.id, change.id)}
                primary={true}
              />
            ) : (
              "(Selected)"
            )}
          </div>
        </InspectorRow>
      ) : null}
    </Fragment>
  );
};

const renderPropertyChange = (props, change) => {
  return (
    <InspectorRow key={`${change.id}`} label={change.label}>
      <div>
        {!syncValueComparisons.valuesAreEqual(change.lastSyncValue, change.externalValue) &&
        change.action === syncUpdateActions.APPLY_EXTERNAL_TO_LOCAL ? (
            <div className="syncProperty">
              {change.lastSyncValue ? <div className="oldValue">{change.lastSyncValue}</div> : null}
              <div className="newValue">{standardizeLines(change.externalValue)}</div>
            </div>
          ) : null}
        {!syncValueComparisons.valuesAreEqual(change.lastSyncValue, change.localValue) &&
        change.action === syncUpdateActions.APPLY_LOCAL_TO_EXTERNAL ? (
            <div className="syncProperty">
              {change.lastSyncValue ? <div className="oldValue">{change.lastSyncValue}</div> : null}
              <div className="newValue">{change.localValue}</div>
            </div>
          ) : null}
      </div>
    </InspectorRow>
  );
};

const renderKeywordList = (list, listItemClass) => {
  return (
    <Fragment>
      {list.map(c => (
        <div key={c.label} className={listItemClass}>
          {c.label}
        </div>
      ))}
    </Fragment>
  );
};

const renderKeywordListChanges = (title, added, removed) => {
  const addedGroup = added.size > 0 ? renderKeywordList(added, "newValue") : null;
  const removedGroup = removed.size > 0 ? renderKeywordList(removed, "oldValue") : null;

  return (
    <InspectorRow key={title} label={title}>
      <div className="syncProperty">
        {removedGroup}
        {addedGroup}
      </div>
    </InspectorRow>
  );
};

const renderConflictGroup = props => {
  const propertyConflicts = props.propertyChanges.filter(
    change =>
      change.status === syncStatusOptions.DETECTED_CONFLICT ||
      (!syncValueComparisons.valuesAreEqual(change.lastSyncValue, change.externalValue) &&
        change.action !== syncUpdateActions.APPLY_EXTERNAL_TO_LOCAL) ||
      (!syncValueComparisons.valuesAreEqual(change.lastSyncValue, change.localValue) &&
        change.action !== syncUpdateActions.APPLY_LOCAL_TO_EXTERNAL)
  );
  const conflictGroup = propertyConflicts.size ? (
    <InspectorGroup label="Conflicts">
      {propertyConflicts.map(change => renderPropertyConflict(props, change)).toArray()}
    </InspectorGroup>
  ) : null;

  return conflictGroup;
};

const renderStatusGroup = props => {
  let timelineChangesGroup = [];
  let externalChangesGroup = [];

  // Divide changes into Timeline/external

  const timelinePropertyChanges = props.propertyChanges.filter(c =>
    [
      syncStatusOptions.DETECTED_CHANGE_IN_LOCAL,
      syncStatusOptions.DETECTED_DELETE_IN_LOCAL,
      syncStatusOptions.DETECTED_CREATE_IN_LOCAL,
      syncStatusOptions.DETECTED_MOVE_IN_LOCAL,
      syncStatusOptions.DETECTED_CHANGE_IN_BOTH
    ].includes(c.status)
  );
  const externalPropertyChanges = props.propertyChanges.filter(c =>
    [
      syncStatusOptions.DETECTED_CHANGE_IN_EXTERNAL,
      syncStatusOptions.DETECTED_DELETE_IN_EXTERNAL,
      syncStatusOptions.DETECTED_CREATE_IN_EXTERNAL,
      syncStatusOptions.DETECTED_MOVE_IN_EXTERNAL
    ].includes(c.status)
  );
  const relationshipsAddedToTimeline = props.relationshipChanges.filter(
    c => c.action === syncUpdateActions.APPLY_CREATE_TO_EXTERNAL
  );
  const relationshipsRemovedFromTimeline = props.relationshipChanges.filter(
    c => c.action === syncUpdateActions.APPLY_DELETE_TO_EXTERNAL
  );
  const relationshipsAddedToExternal = props.relationshipChanges.filter(
    c => c.action === syncUpdateActions.APPLY_CREATE_TO_LOCAL
  );
  const relationshipsRemovedFromExternal = props.relationshipChanges.filter(
    c => c.action === syncUpdateActions.APPLY_DELETE_TO_LOCAL
  );
  const tagsAddedToTimeline = props.tagChanges.filter(c => c.action === syncUpdateActions.APPLY_CREATE_TO_EXTERNAL);
  const tagsRemovedFromTimeline = props.tagChanges.filter(c => c.action === syncUpdateActions.APPLY_DELETE_TO_EXTERNAL);
  const tagsAddedToExternal = props.tagChanges.filter(c => c.action === syncUpdateActions.APPLY_CREATE_TO_LOCAL);
  const tagsRemovedFromExternal = props.tagChanges.filter(c => c.action === syncUpdateActions.APPLY_DELETE_TO_LOCAL);

  // Render changes

  if (timelinePropertyChanges.size) {
    timelineChangesGroup.push(timelinePropertyChanges.map(change => renderPropertyChange(props, change)).toArray());
  }
  if (externalPropertyChanges.size) {
    externalChangesGroup.push(externalPropertyChanges.map(change => renderPropertyChange(props, change)).toArray());
  }

  if (relationshipsAddedToTimeline.size || relationshipsRemovedFromTimeline.size) {
    timelineChangesGroup.push(
      renderKeywordListChanges("Relationships", relationshipsAddedToTimeline, relationshipsRemovedFromTimeline)
    );
  }
  if (relationshipsAddedToExternal.size || relationshipsRemovedFromExternal.size) {
    externalChangesGroup.push(
      renderKeywordListChanges("Relationships", relationshipsAddedToExternal, relationshipsRemovedFromExternal)
    );
  }
  if (tagsAddedToTimeline.size || tagsRemovedFromTimeline.size) {
    timelineChangesGroup.push(renderKeywordListChanges("Tags", tagsAddedToTimeline, tagsRemovedFromTimeline));
  }
  if (tagsAddedToExternal.size || tagsRemovedFromExternal.size) {
    externalChangesGroup.push(renderKeywordListChanges("Tags", tagsAddedToExternal, tagsRemovedFromExternal));
  }

  return (
    <Fragment>
      {timelineChangesGroup.length ? (
        <InspectorGroup label="Timeline Changes">{timelineChangesGroup}</InspectorGroup>
      ) : null}
      {externalChangesGroup.length ? (
        <InspectorGroup label={`${props.syncApplicationName} Changes`}>{externalChangesGroup}</InspectorGroup>
      ) : null}
    </Fragment>
  );
};

const renderNormalContent = props => {
  const isLinked =
    props.status !== syncStatusOptions.DETECTED_CREATE_IN_EXTERNAL &&
    props.status !== syncStatusOptions.DETECTED_CREATE_IN_LOCAL &&
    props.status !== syncStatusOptions.DETECTED_DELETE_IN_EXTERNAL &&
    props.status !== syncStatusOptions.DETECTED_DELETE_IN_LOCAL;

  return (
    <Fragment>
      {renderTopGroup(props)}
      {renderSyncStatusGroup(props)}
      <div className="syncInspectorChanges">
        <div className="inspectorHeightAutoGrowArea">
          {renderLinkGroup(props)}
          {isLinked ? (
            <Fragment>
              {renderConflictGroup(props)}
              {renderStatusGroup(props)}
            </Fragment>
          ) : null}
        </div>
      </div>
    </Fragment>
  );
};

const SyncItemInspector = props => {
  return (
    <div className="inspector">
      {props.destinationPath.size > 1 ? renderNormalContent(props) : renderManuscriptGroup(props)}
    </div>
  );
};

SyncItemInspector.propTypes = {
  id: PropTypes.string,
  syncApplicationName: PropTypes.string,
  isIgnored: PropTypes.bool,
  externalId: PropTypes.string,
  externalLabel: PropTypes.string,
  externalType: PropTypes.string,
  externalTypeIcon: PropTypes.string,
  externalTypeColor: PropTypes.string,
  localId: PropTypes.string,
  localLabel: PropTypes.string,
  localTypeIcon: PropTypes.string,
  localTypeColor: PropTypes.string,
  status: PropTypes.string,
  destinationPath: ImmutablePropTypes.listOf(PropTypes.string),
  propertyChanges: ImmutablePropTypes.listOf(
    PropTypes.instanceOf(immutableRecords.InspectorSyncItemPropertyChangeRecord)
  ),
  tagChanges: ImmutablePropTypes.listOf(PropTypes.instanceOf(immutableRecords.InspectorSyncItemTagChangeRecord)),
  relationshipChanges: ImmutablePropTypes.listOf(
    PropTypes.instanceOf(immutableRecords.InspectorSyncItemRelationshipChangeRecord)
  ),
  unlinkedDocumentOptions: ImmutablePropTypes.listOf(PropTypes.instanceOf(immutableRecords.DropdownOptionRecord)),
  unlinkedItemOptions: ImmutablePropTypes.listOf(PropTypes.instanceOf(immutableRecords.DropdownOptionRecord)),

  // Dispatch actions
  onChangeLink: PropTypes.func.isRequired,
  onCollapseClick: PropTypes.func.isRequired,
  onChooseLocalClick: PropTypes.func.isRequired,
  onChooseExternalClick: PropTypes.func.isRequired,
  onSelectItems: PropTypes.func.isRequired
};

// Copy PropTypes across to other functions
renderNormalContent.propTypes = SyncItemInspector.propTypes;
renderManuscriptGroup.propTypes = SyncItemInspector.propTypes;
renderTopGroup.propTypes = SyncItemInspector.propTypes;
renderSyncStatusGroup.propTypes = SyncItemInspector.propTypes;
renderLinkGroup.propTypes = SyncItemInspector.propTypes;
renderPropertyConflict.propTypes = SyncItemInspector.propTypes;
renderPropertyChange.propTypes = SyncItemInspector.propTypes;
renderKeywordListChanges.propTypes = SyncItemInspector.propTypes;
renderConflictGroup.propTypes = SyncItemInspector.propTypes;
renderStatusGroup.propTypes = SyncItemInspector.propTypes;

const mapStateToProps = state => {
  const inspectorData = selectors.getSyncItemStatus(state.present);
  const syncApplicationName = syncSettingsSelectors.getSyncApplicationName(state.present);

  const unlinkedDocumentOptions = selectors.getUnlinkedDocumentOptionsForItem(state.present);
  const unlinkedItemOptions = selectors.getUnlinkedItemOptionsForDocument(state.present);

  return {
    syncApplicationName,
    unlinkedDocumentOptions,
    unlinkedItemOptions,
    ...inspectorData.toObject()
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onCollapseClick: () => {
      // #!if iOS === true
// dispatch(batchOperations.hideLeftPanelAndClearSelection());
      // #!else
      dispatch(appStateOperations.hideRightPanel());
      // #!endif
    },
    onChooseLocalClick: (docId, fieldId) => {
      dispatch(syncOperations.chooseLocalSyncValue(docId, fieldId));
    },
    onChooseExternalClick: (docId, fieldId) => {
      dispatch(syncOperations.chooseExternalSyncValue(docId, fieldId));
    },
    onChangeLink: (localId, externalId, oldLocalId, oldExternalId) => {
      let action = null;

      if (localId === SyncTypeOptions.IGNORE) {
        action = syncOperations.ignoreExternalIds([externalId]);
      } else {
        if (externalId) {
          action = syncOperations.addUserCreatedLinks([
            {
              localId,
              externalId
            }
          ]);
        } else {
          action = batchActions([
            syncOperations.clearUserCreatedLinksForLocalIds([localId]),
            syncOperations.unignoreExternalIds([oldExternalId])
          ]);
        }
      }
      dispatch(action);
    },
    onSelectItems: itemIds => {
      dispatch(appStateOperations.selectItems(itemIds));
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SyncItemInspector);
